import Vue from 'vue';
import VModal from 'vue-js-modal';
import store from './store/marketplace';
import {mapState} from 'vuex';
import vSelect from 'vue-select';
import VueScrollTo from 'vue-scrollto';
import './core/bugsnag';
import PortalVue from 'portal-vue';
import Form from './core/Form';

Vue.use(PortalVue);
Vue.use(VueScrollTo);
Vue.use(VModal);
Vue.component('loading', require('./components/Loading').default);
Vue.component('v-select', vSelect);
Vue.component('item-listing', require('./components/ItemListing').default);
Vue.filter('currency', price => '£' + (price / 100).toFixed(2));

new Vue({
    el: '#marketplace',
    store,
    data() {
        return {
            isLoading: true,
            items: [],
            form: null,
            maxPages: 5,
            showingMobileFilters: false,
            presetParams: null,
            sortByOptions: [
                {code: 'price-lowest', label: 'Price: Lowest first'},
                {code: 'price-highest', label: 'Price: Highest first'},
                {code: 'fps-lowest', label: 'FPS: Lowest first'},
                {code: 'fps-highest', label: 'FPS: Highest first'},
                {code: 'date-oldest', label: 'Date added: Oldest first'},
                {code: 'date-newest', label: 'Date added: Newest first'},
            ],
            perPageOptions: [
                {code: 12, label: 12},
                {code: 24, label: 24},
                {code: 48, label: 48},
                {code: 'all', label: 'all'},
            ],
        };
    },

    created() {
        let searchParams = new URLSearchParams(window.location.search.substring(1));
        if (searchParams.has('category')) {
            this.presetParams = Object.assign(this.presetParams ?? {}, {categories: [searchParams.get('category')]});
        }
        if (searchParams.has('search')) {
            this.presetParams = Object.assign(this.presetParams ?? {}, {search: searchParams.get('search')});
        }
    },

    mounted() {
        this.form = new Form(Object.assign({
            types: ['1', '2'],
            categories: ['pistols', 'shotguns', 'assault-rifles', 'sniper-rifles', 'pyros', 'tactical-gear', 'gun-accessories', 'other', 'spares-repairs'],
            conditions: ['1', '2', '3', '4'],
            price: {
                min: 0,
                max: 250000,
            },
            sortBy: 'date-newest',
            search: null,
            perPage: 24,
            currentCount: 0,
            totalItemCount: 0,
        }, this.presetParams ? this.presetParams : Object.assign(this.filters ?? {}, {search: null})));

        this.submitFilterChangeForm();
    },

    computed: {
        ...mapState(['filters']),
        priceFiltersValid() {
            return parseInt(this.form.data.price.min) >= 0 && parseInt(this.form.data.price.max) && this.form.data.price.min < this.form.data.price.max;
        },

        canGoToNextPage() {
            return this.form.data.totalItemCount > this.form.data.currentCount + this.items.length;
        },

        canGoToPreviousPage() {
            return this.form.data.totalItemCount < this.form.data.currentCount - this.items.length;
        },

        pagination() {
            if (this.form.data.perPage === 'all') {
                return null;
            }

            let paginationConfig = {
                totalItems: this.form.data.totalItemCount,
                currentPage: this.form.data.currentCount / this.form.data.perPage + 1,
                pageSize: this.form.data.perPage,
                totalPages: Math.ceil(this.form.data.totalItemCount / this.form.data.perPage),
                startPage: 0,
                endPage: 0,
                startIndex: 0,
                endIndex: 0,
                pages: [],
            };

            if (paginationConfig.totalPages <= this.maxPages) {
                // Total pages less than max so show all pages
                paginationConfig.startPage = 1;
                paginationConfig.endPage = paginationConfig.totalPages;
            } else {
                // Total pages more than max so calculate start and end pages
                let maxPagesBeforeCurrentPage = Math.floor(this.maxPages / 2),
                    maxPagesAfterCurrentPage = Math.ceil(this.maxPages / 2) - 1;
                if (paginationConfig.currentPage <= maxPagesBeforeCurrentPage) {
                    // Current page near the start
                    paginationConfig.startPage = 1;
                    paginationConfig.endPage = this.maxPages;
                } else if (paginationConfig.currentPage + maxPagesAfterCurrentPage >= paginationConfig.totalPages) {
                    // Current page near the end
                    paginationConfig.startPage = paginationConfig.totalPages - this.maxPages + 1;
                    paginationConfig.endPage = paginationConfig.totalPages;
                } else {
                    // Current page somewhere in the middle
                    paginationConfig.startPage = paginationConfig.currentPage - maxPagesBeforeCurrentPage;
                    paginationConfig.endPage = paginationConfig.currentPage + maxPagesAfterCurrentPage;
                }
            }

            // Calculate start and end item indexes
            paginationConfig.startIndex = (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
            paginationConfig.endIndex = Math.min(paginationConfig.startIndex + paginationConfig.pageSize - 1, paginationConfig.totalItems - 1);
            // Create an array of pages to ng-repeat in the pager control
            paginationConfig.pages = Array.from(Array((paginationConfig.endPage + 1) - paginationConfig.startPage).keys()).map(i => paginationConfig.startPage + i);

            return paginationConfig;
        },
    },

    methods: {
        submitFilterChangeForm(resetItemCount = false) {
            if (this.isShowingMobileFilters) {
                return;
            }

            this.isLoading = true;

            if (resetItemCount) {
                this.form.data.currentCount = 0;
            }

            this.$scrollTo('#marketplace', 500);

            this.form.submit('post', route('marketplace.filter'))
                .then(response => {
                    this.items = response.items;
                    if (this.items.length) {
                        store.commit('setFilters', this.form.data);
                    }
                    this.form.data.totalItemCount = response.totalItemCount;
                })
                .catch(error => console.log(error))
                .finally(() => this.isLoading = false);
        },

        nextPage(event) {
            event.target.blur();
            if (!this.canGoToNextPage) {
                return;
            }

            this.form.data.currentCount += parseInt(this.pagination.pageSize);
            this.submitFilterChangeForm();
        },

        previousPage(event) {
            event.target.blur();
            if (!this.canGoToPreviousPage) {
                return;
            }

            this.form.data.currentCount -= parseInt(this.pagination.pageSize);
            this.submitFilterChangeForm();
        },

        goToPage(event, page) {
            event.target.blur();
            if (page == this.pagination.currentPage) {
                return;
            }

            this.form.data.currentCount = (page - 1) * this.pagination.pageSize;
            this.submitFilterChangeForm();
        },

        openMobileFilters() {
            this.isShowingMobileFilters = true;
            this.$modal.show('mobile-filters');
        },

        closeMobileFilters() {
            this.isShowingMobileFilters = false;
            this.submitFilterChangeForm(true);
        },

        clearSearch() {
            this.form.data.search = null;
            this.submitFilterChangeForm(true);
            history.pushState(null, 'Marketplace - Airsoft Hub Buy & Sell Used Airsoft Equipment', '/marketplace');
        },
    },
});
